import type { FoodWarning, HiddenReason } from '@amumin/api-client';
import {
  canonical,
  FOOD_RULES,
  INGREDIENT_FLAGS,
  ruleConflicts,
  type IngredientFlag,
} from '@amumin/domain/profile';
import { t, type AppMessages } from '@amumin/i18n';

// Tekst bij het gezinsprofiel in de webapp (0024; docs/DESIGN.md §8.P.5/§8.P.6): "Verborgen omdat
// jullie halal eten", de kop per groep bij "Alles tonen" en de eetmelding ("Hier zit spekjes in.
// Jullie eten halal."). De redenen komen van de server (hidden_reason); hier alleen woorden.
// Nooit een traditie als oorzaak ("Turks"), alleen de keuze van het gezin.

/** Waar een ouder het profiel wijzigt (builder A, §8.P.3). */
export const PROFILE_SETTINGS_PATH = '/instellingen/wat-past';

type P = AppMessages['profile'];

/** "a, b en c" in de taal van de app. */
export function joinList(parts: ReadonlyArray<string>, and: string): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} ${and} ${parts[parts.length - 1]}`;
}

function faithNames(p: P, faiths: ReadonlyArray<string>): string {
  const labels = p.faiths.labels as Record<string, string>;
  return joinList(
    faiths.map((f) => labels[f] ?? f),
    p.warning.and,
  );
}

/** "Turks en Islam" (Sohbet): de keuzes die het gezin kan maken, nooit als oorzaak. */
function cultureNames(
  p: P,
  kitchens: ReadonlyArray<string>,
  faiths: ReadonlyArray<string>,
): string {
  const labels = p.kitchens as Record<string, string>;
  const faithLabels = p.faiths.labels as Record<string, string>;
  return joinList(
    [...kitchens.map((k) => labels[k] ?? k), ...faiths.map((f) => faithLabels[f] ?? f)],
    p.warning.and,
  );
}

/** Naam van een gewoonte (golf 6b): "Koranles" (Turks: "Kuran dersi"). */
function practiceName(p: P, practice: string): string {
  const names = p.screens.practices as Record<string, string>;
  return names[practice] ?? practice;
}

function rulePhrases(p: P, rules: ReadonlyArray<string>): string {
  const phrase = p.hidden.rulePhrase as Record<string, string>;
  return joinList(
    rules.map((r) => phrase[r] ?? r),
    p.warning.and,
  );
}

/** Sleutel om verborgen opties per reden te groeperen (één kop per groep). */
export function reasonGroupKey(r: HiddenReason): string {
  switch (r.kind) {
    case 'faith':
      return r.none ? 'faith:none' : 'faith';
    case 'not_chosen':
      // Eén groep per geloof, zodat de kop zegt welk geloof ("Verschijnt als je Christendom kiest").
      return `not_chosen:${r.faiths.join(',')}`;
    case 'faith_open':
      return 'faith_open';
    case 'food':
      return `food:${r.rules.join(',')}`;
    case 'variant':
      return 'variant';
    case 'practice':
      return 'practice';
    case 'culture':
      return `culture:${r.kitchens.join(',')}:${r.faiths.join(',')}`;
  }
}

/** Kop boven een groep verborgen opties na "Alles tonen". */
export function reasonHeading(p: P, r: HiddenReason, noneHeading: string): string {
  switch (r.kind) {
    case 'faith':
      return r.none ? noneHeading : p.hidden.groupFaith;
    case 'not_chosen':
      return t(p.hidden.groupNotChosen, { what: faithNames(p, r.faiths) });
    case 'faith_open':
      return p.hidden.groupFaithOpen;
    case 'food':
      return t(p.hidden.groupFood, { rules: rulePhrases(p, r.rules) });
    case 'variant':
      return p.hidden.variant;
    case 'practice':
      return p.hidden.groupPractice;
    case 'culture':
      return t(p.hidden.groupNotChosen, { what: cultureNames(p, r.kitchens, r.faiths) });
  }
}

/** Reden bij één verborgen optie (zoekresultaten). */
export function reasonText(p: P, r: HiddenReason): string {
  switch (r.kind) {
    case 'faith':
      return r.none ? p.hidden.faithNone : t(p.hidden.faith, { faiths: faithNames(p, r.faiths) });
    case 'not_chosen':
      return t(p.hidden.notChosen, { what: faithNames(p, r.faiths) });
    case 'faith_open':
      return p.hidden.faithOpen;
    case 'food':
      return t(p.hidden.food, { rules: rulePhrases(p, r.rules) });
    case 'variant':
      return p.hidden.variant;
    case 'practice':
      return t(p.hidden.practice, { practice: practiceName(p, r.practice) });
    case 'culture':
      return t(p.hidden.notChosen, { what: cultureNames(p, r.kitchens, r.faiths) });
  }
}

/** Groepen verborgen items in serverorde, met de kop van de eerste reden. */
export function groupHidden<T extends { hidden_reason: HiddenReason }>(
  p: P,
  hidden: ReadonlyArray<T>,
  noneHeading: string,
): Array<{ key: string; heading: string; items: T[] }> {
  const out: Array<{ key: string; heading: string; items: T[] }> = [];
  for (const h of hidden) {
    const key = reasonGroupKey(h.hidden_reason);
    const group = out.find((g) => g.key === key);
    if (group) group.items.push(h);
    else out.push({ key, heading: reasonHeading(p, h.hidden_reason, noneHeading), items: [h] });
  }
  return out;
}

export interface WarningLines {
  /** "Hier zit spekjes in." (of "Let op: dit bevat misschien varkensvlees.") */
  what: string;
  /** "Jullie eten halal." / "Anne eet vegetarisch." (één zin per regel). */
  who: string[];
  /** "Tip: kipspekjes in plaats van spekjes." of null. */
  tip: string | null;
  /** Alleen "let op" (twijfelgeval zonder ruil): alleen "Oké". */
  checkOnly: boolean;
}

/** Eerste letter klein ("Spekkies" midden in een zin: "in plaats van spekkies"). */
function lowerFirst(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

/**
 * De vlag waar het om gaat bij één boodschap: de eerste die met een geraakte regel botst
 * ("Spekkies" bij halal: gelatine, niet het woord zelf).
 */
function clashingFlag(w: FoodWarning, flags: ReadonlyArray<string>): IngredientFlag | null {
  const known = canonical(flags, INGREDIENT_FLAGS);
  const rules = canonical(
    w.by_rule.map((b) => b.rule),
    FOOD_RULES,
  );
  for (const c of ruleConflicts({ flags: known, maybe: [] }, rules)) {
    const flag = c.flags[0];
    if (flag) return flag;
  }
  return known[0] ?? null;
}

/**
 * De zinnen van een eetmelding (§8.P.6): eerst wat, dan wie. `item`: de melding gaat over één
 * boodschap (niet over een gerecht). Dan zegt de eerste zin WAT het probleem is ("Hier zit meestal
 * varkensgelatine in.") in plaats van "Hier zit Spekkies in.", en staat de naam in de tip met een
 * kleine letter (review d-ux r3).
 */
export function warningLines(p: P, w: FoodWarning, opts: { item?: boolean } = {}): WarningLines {
  const w8 = p.warning;
  const flags = p.flags as Record<string, string>;
  const first = w.ingredients[0];
  // "Jullie ..." alleen voor een regel die echt iedereen aan tafel raakt (gezin halal + Anne
  // vegetarisch: "Jullie eten halal. Anne eet vegetarisch."). Ook bij een twijfelgeval (gewoon
  // gehakt) noemen we wie het betreft, met een ruiltip als die er is (review d-ux r2).
  const affected = new Set<string>();
  for (const b of w.by_rule) for (const id of b.member_ids) affected.add(id);
  const who = w.by_rule.map((b) => {
    if (w.everyone && b.member_ids.length >= affected.size)
      return (w8.everyone as Record<string, string>)[b.rule] ?? '';
    const table = (b.names.length === 1 ? w8.person : w8.people) as Record<string, string>;
    return t(table[b.rule] ?? '', { names: joinList(b.names, w8.and) });
  });
  const swap = w.swaps[0];
  const tip = swap
    ? t(w8.swap, {
        swap: swap.swap,
        ingredient: opts.item ? lowerFirst(swap.ingredient) : swap.ingredient,
      })
    : null;
  if (w.severity === 'check') {
    const what = first ? (first.flags.map((f) => flags[f] ?? f)[0] ?? first.name) : '';
    return {
      what: t(w8.check, { what }),
      who: who.filter((x) => x.length > 0),
      tip,
      // Zonder ruil valt er niets te kiezen: alleen "Oké".
      checkOnly: !swap,
    };
  }
  // Koosjer: vlees met zuivel samen, zonder ingrediënt dat op zich botst (review d-reg r3).
  if (w.meat_dairy) {
    return {
      what: t(w8.meatDairy, { meat: w.meat_dairy.meat, dairy: w.meat_dairy.dairy }),
      who: who.filter((x) => x.length > 0),
      tip,
      checkOnly: false,
    };
  }
  const itemFlag = opts.item && first ? clashingFlag(w, first.flags) : null;
  return {
    what: itemFlag
      ? ((w8.item as Record<string, string>)[itemFlag] ?? '')
      : first
        ? t(looksPlural(first.name) ? w8.containsMany : w8.contains, { ingredient: first.name })
        : '',
    who: who.filter((x) => x.length > 0),
    tip,
    checkOnly: false,
  };
}

/**
 * Meervoud in het Nederlands ("spekjes", "kippendijen", "garnalen"): dan "Hier zitten ... in".
 * Eenvoudig op de uitgang van het laatste woord; "kaas" en "rookworst" blijven enkelvoud.
 */
export function looksPlural(name: string): boolean {
  const last = name.trim().toLowerCase().split(/\s+/).pop() ?? '';
  return /(en|jes|'s)$/.test(last);
}

/** Eén zin voor een tooltip of voorleestekst (weekmenu-pictogram). */
export function warningSentence(p: P, w: FoodWarning): string {
  const l = warningLines(p, w);
  return [l.what, ...l.who].filter(Boolean).join(' ');
}

/**
 * De titel na een ruil: staat het geruilde ingrediënt in de naam ("Hutspot met rookworst"), dan
 * komt de ruil erin ("Hutspot met halal runderrookworst"), zodat het weekmenu en de tablet niet
 * meer "rookworst" zeggen. Anders blijft de titel gelijk.
 */
export function swappedTitle(title: string, swap: { ingredient: string; swap: string }): string {
  const words = swap.ingredient
    .toLowerCase()
    .split(/[^a-zà-ÿ-]+/)
    .filter((w) => w.length >= 3)
    .sort((x, y) => y.length - x.length);
  for (const w of words) {
    const at = title.toLowerCase().indexOf(w);
    if (at < 0) continue;
    // Aan het begin van de titel met een hoofdletter ("Gehaktballen" -> "Rundergehaktballen").
    const put = at === 0 ? swap.swap.charAt(0).toUpperCase() + swap.swap.slice(1) : swap.swap;
    return title.slice(0, at) + put + title.slice(at + w.length);
  }
  return title;
}
